import { Address } from "@ton/core";
import {
  COMMITMENT_DOMAIN,
  NULLIFIER_DOMAIN,
  type AccountState,
  type Client,
  type GetTransactionsResult,
  type RunMethodArg,
  type RunMethodResult,
  type TransactionCursor,
} from "@tonresistor/zkresistor-sdk";
import type {
  CatalogResponse,
  PoolKind,
  PoolViewResponse,
  ProtocolApi,
  ProtocolManifest,
} from "./contracts.js";

const ADDRESS_FORMAT = { bounceable: true, urlSafe: true } as const;

function canonicalAddress(value: string): string {
  return Address.parse(value).toString(ADDRESS_FORMAT);
}

function sameAddress(left: string, right: string): boolean {
  return Address.parse(left).equals(Address.parse(right));
}

export class CatalogClient implements Client {
  private readonly accounts = new Map<string, AccountState>();

  constructor(
    readonly catalog: CatalogResponse,
    manifest: ProtocolManifest,
  ) {
    if (!sameAddress(catalog.factory.address, manifest.factoryAddress)) {
      throw new Error("Catalog Factory does not match the pinned protocol manifest");
    }
    this.accounts.set(canonicalAddress(catalog.factory.address), catalog.factory.account);
    for (const pool of catalog.pools) {
      const key = canonicalAddress(pool.address);
      if (this.accounts.has(key)) throw new Error(`Catalog repeats account ${key}`);
      this.accounts.set(key, pool.account);
    }
  }

  async getAccountState(address: string): Promise<AccountState> {
    const account = this.accounts.get(canonicalAddress(address));
    if (account === undefined) throw new Error("CatalogClient rejected an unregistered address");
    return account;
  }

  runMethod(
    _address: string,
    _method: string,
    _params: readonly RunMethodArg[],
  ): Promise<RunMethodResult> {
    return Promise.reject(new Error("CatalogClient does not expose contract getters"));
  }

  getTransactions(
    _address: string,
    _limit: number,
    _before: TransactionCursor | undefined,
  ): Promise<GetTransactionsResult> {
    return Promise.reject(new Error("CatalogClient does not expose transaction history"));
  }
}

export class PoolViewClient implements Client {
  private view: PoolViewResponse | null = null;

  constructor(
    private readonly api: ProtocolApi,
    readonly poolAddress: string,
    readonly kind: PoolKind,
  ) {
    this.poolAddress = canonicalAddress(poolAddress);
  }

  async openView(): Promise<PoolViewResponse> {
    const next = await this.api.openPoolView(this.poolAddress, this.kind);
    if (!sameAddress(next.poolAddress, this.poolAddress) || next.kind !== this.kind) {
      throw new Error("Gateway returned a view for another Pool");
    }
    this.view = next;
    return next;
  }

  private async currentView(): Promise<PoolViewResponse> {
    return this.view ?? this.openView();
  }

  activeView(): PoolViewResponse | null {
    return this.view;
  }

  async getAccountState(address: string): Promise<AccountState> {
    if (!sameAddress(address, this.poolAddress)) {
      throw new Error("PoolViewClient rejected an address outside its Pool");
    }
    return (await this.currentView()).account;
  }

  async runMethod(
    address: string,
    method: string,
    params: readonly RunMethodArg[],
  ): Promise<RunMethodResult> {
    if (!sameAddress(address, this.poolAddress) || method !== "sparseRoot") {
      throw new Error("PoolViewClient rejected a non-protocol getter");
    }
    if (
      params.length !== 2 ||
      typeof params[0] !== "string" ||
      typeof params[1] !== "string" ||
      !/^(?:0|[1-9][0-9]*)$/.test(params[0]) ||
      !/^(?:0|[1-9][0-9]*)$/.test(params[1])
    ) throw new Error("sparseRoot arguments are not canonical unsigned integers");
    const domain = Number(params[0]);
    const bucket = Number(params[1]);
    if (!Number.isInteger(bucket) || bucket < 0 || bucket > 255) {
      throw new RangeError("sparseRoot bucket must be a uint8");
    }
    const view = await this.currentView();
    const roots = domain === COMMITMENT_DOMAIN
      ? view.checkpoint.commitmentSeenRoots
      : domain === NULLIFIER_DOMAIN
        ? view.checkpoint.nullifierSpentRoots
        : null;
    if (roots === null) throw new RangeError("sparseRoot domain is not supported");
    const root = roots[bucket];
    if (root === undefined) throw new Error("Gateway sparse-root table is incomplete");
    return { exit_code: 0, stack: [BigInt(`0x${root}`).toString()] };
  }

  async getTransactions(
    address: string,
    _limit: number,
    _before: TransactionCursor | undefined,
  ): Promise<GetTransactionsResult> {
    if (!sameAddress(address, this.poolAddress)) {
      throw new Error("PoolViewClient rejected transaction history for another address");
    }
    return Promise.reject(new Error("PoolViewClient does not expose transaction history"));
  }
}
